import { Fragment, useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  InboxIcon,
  SparklesIcon,
  UserGroupIcon,
  UsersIcon,
  CpuChipIcon,
  TableCellsIcon,
  ChevronLeftIcon,
  ArrowUpTrayIcon,
  PaintBrushIcon,
  MegaphoneIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuthStore } from '@/store/auth';
import { TerraPanel, TerraToggle } from '@/components/terra';
import { FeedbackButton } from '@/components/feedback';
import { useUnreadFeedback } from '@/hooks/useUnreadFeedback';
import { useTerraStore } from '@/store/terra';
import { useTheme } from '@/pages/data-vault/components/ThemeToggle';
import { useLogout } from '@/hooks/useAuth';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { PRODUCTS_CATALOG } from '@/types/products';
import { authApi } from '@/api/auth';
import { PolygonIcon } from '@/components/icons/PolygonIcon';
import { PhreaticIcon } from '@/components/icons/PhreaticIcon';
import { PulseWaveIcon } from '@/components/icons/PulseWaveIcon';
import { PathfinderIcon } from '@/components/icons/PathfinderIcon';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  CubeTransparentIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  TableCellsIcon,
  PolygonIcon,
  PhreaticIcon,
  PulseWaveIcon,
  PathfinderIcon,
};

const productRouteOverrides: Record<string, string> = {
  datavault: '/data-vault',
};

const navigation = [
  { name: 'PRODUCTS', href: '/dashboard', icon: Squares2X2Icon },
];


export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const { theme, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const { user, products, setProducts, viewAsRole, setViewAsRole, getEffectiveRole } = useAuthStore();
  const terraOpen = useTerraStore((s) => s.isOpen);
  const logoutMutation = useLogout();
  const effectiveRole = getEffectiveRole();
  const unreadCount = useUnreadFeedback();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  // Fetch user's products from /auth/me on mount if store is empty
  useEffect(() => {
    if (products.length === 0) {
      authApi.getMe().then((me) => {
        if (me.products && me.products.length > 0) {
          setProducts(me.products);
        }
      }).catch(() => {
        // Silently fail — products will just be empty
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isTrueSuperadmin = user?.role === 'superadmin';
  const isAdmin = effectiveRole === 'admin' || effectiveRole === 'superadmin';
  const isSuperadmin = effectiveRole === 'superadmin';

  const subscribedProducts = PRODUCTS_CATALOG.filter((p) =>
    products.includes(p.slug)
  );

  return (
    <div className={`min-h-screen bg-mono-950 transition-[margin] duration-300 ${terraOpen ? 'mr-[420px]' : ''}`}>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-mono-950/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-mono-900 border-r border-mono-800 px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center border-b border-mono-800">
                    <Link to="/" className="flex items-center">
                      <img src="/argos-logo.png" alt="ARGOS" className="h-14 w-auto object-contain" />
                    </Link>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                onClick={() => setSidebarOpen(false)}
                                className={clsx(
                                  location.pathname === item.href
                                    ? 'bg-mono-800 text-mono-100 border-l-2 border-mono-100'
                                    : 'text-mono-400 hover:bg-mono-800 hover:text-mono-200 border-l-2 border-transparent',
                                  'group flex gap-x-3 p-2 text-xs font-medium tracking-wider'
                                )}
                              >
                                <item.icon className="h-5 w-5 shrink-0" />
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className={clsx(
        'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300',
        collapsed ? 'lg:w-14' : 'lg:w-56'
      )}>
        <div className={clsx(
          'flex grow flex-col gap-y-5 overflow-y-auto bg-mono-900 border-r border-mono-800 pb-4 transition-all duration-300',
          collapsed ? 'px-2' : 'px-4'
        )}>
          <div className="flex h-16 shrink-0 items-center border-b border-mono-800">
            <div className="flex items-center gap-2 flex-1 overflow-hidden">
              <Link to="/" className="flex items-center overflow-hidden">
                {collapsed ? (
                  <img src="/logo-icon.png" alt="ARGOS" className="h-7 w-7 shrink-0 object-contain" />
                ) : (
                  <img src="/argos-logo.png" alt="ARGOS" className="h-14 w-auto object-contain" />
                )}
              </Link>
            </div>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1 text-mono-500 hover:text-mono-300 transition-colors shrink-0"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeftIcon className={clsx('h-4 w-4 transition-transform duration-300', collapsed && 'rotate-180')} />
            </button>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        title={collapsed ? item.name : undefined}
                        className={clsx(
                          location.pathname === item.href
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-mono-100'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {!collapsed && item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>

              {/* Superadmin: Feedback inbox (appears above TEAM) */}
              {isSuperadmin && (
                <li className="-mt-5">
                  <ul className="-mx-2 space-y-1">
                    <li>
                      <Link
                        to="/feedback"
                        title={collapsed ? 'FEEDBACK' : undefined}
                        className={clsx(
                          location.pathname.startsWith('/feedback')
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-cyan-500'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex items-center gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <div className="relative">
                          <InboxIcon className="h-4 w-4 shrink-0" />
                          {collapsed && unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full" />
                          )}
                        </div>
                        {!collapsed && 'FEEDBACK'}
                        {!collapsed && unreadCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white rounded-full text-[10px] px-1.5 py-0.5 font-bold">
                            {unreadCount}
                          </span>
                        )}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/agents"
                        title={collapsed ? 'AGENTS' : undefined}
                        className={clsx(
                          location.pathname.startsWith('/agents')
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-cyan-500'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex items-center gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <SparklesIcon className="h-4 w-4 shrink-0" />
                        {!collapsed && 'AGENTS'}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/release-notes"
                        title={collapsed ? 'RELEASE NOTES' : undefined}
                        className={clsx(
                          location.pathname.startsWith('/release-notes')
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-cyan-500'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex items-center gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <MegaphoneIcon className="h-4 w-4 shrink-0" />
                        {!collapsed && 'RELEASE NOTES'}
                      </Link>
                    </li>
                  </ul>
                </li>
              )}

              {/* Team Management (superadmin only) */}
              {isSuperadmin && (
                <li className="-mt-5">
                  <ul className="-mx-2 space-y-1">
                    <li>
                      <Link
                        to="/team"
                        title={collapsed ? 'TEAM' : undefined}
                        className={clsx(
                          location.pathname === '/team'
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-mono-100'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <UserGroupIcon className="h-4 w-4 shrink-0" />
                        {!collapsed && 'TEAM'}
                      </Link>
                    </li>
                  </ul>
                </li>
              )}

              {/* Superadmin links */}
              {isSuperadmin && (
                <li className="-mt-5">
                  <ul className="-mx-2 space-y-1">
                    <li>
                      <Link
                        to="/admin/clients"
                        title={collapsed ? 'CLIENTS' : undefined}
                        className={clsx(
                          location.pathname.startsWith('/admin/clients') || location.pathname.startsWith('/admin/inquiries')
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-cyan-500'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <UsersIcon className="h-4 w-4 shrink-0" />
                        {!collapsed && 'CLIENTS'}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/admin/models"
                        title={collapsed ? 'MODELS' : undefined}
                        className={clsx(
                          location.pathname === '/admin/models'
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-cyan-500'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <CpuChipIcon className="h-4 w-4 shrink-0" />
                        {!collapsed && 'MODELS'}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/admin/data-import"
                        title={collapsed ? 'DATA IMPORT' : undefined}
                        className={clsx(
                          location.pathname === '/admin/data-import'
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-cyan-500'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <ArrowUpTrayIcon className="h-4 w-4 shrink-0" />
                        {!collapsed && 'DATA IMPORT'}
                      </Link>
                    </li>
                  </ul>
                </li>
              )}

              {/* Organization Admin — org owner / admins only. Invited seats
                  (analyst/viewer) don't see this; they still see their billing
                  + the shared token balance via the product's billing page. */}
              {isAdmin && (
                <li className="-mt-5">
                  <ul className="-mx-2 space-y-1">
                    <li>
                      <Link
                        to="/account"
                        title={collapsed ? 'ORGANIZATION' : undefined}
                        className={clsx(
                          location.pathname === '/account'
                            ? 'bg-mono-800 text-mono-100 border-l-2 border-cyan-500'
                            : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                          'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                          collapsed && 'justify-center'
                        )}
                      >
                        <BuildingOffice2Icon className="h-4 w-4 shrink-0" />
                        {!collapsed && 'ORGANIZATION'}
                      </Link>
                    </li>
                  </ul>
                </li>
              )}

              {/* Settings */}
              <li className="-mt-5">
                <ul className="-mx-2 space-y-1">
                  <li>
                    <Link
                      to="/settings"
                      title={collapsed ? 'SETTINGS' : undefined}
                      className={clsx(
                        location.pathname === '/settings'
                          ? 'bg-mono-800 text-mono-100 border-l-2 border-mono-100'
                          : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                        'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                        collapsed && 'justify-center'
                      )}
                    >
                      <Cog6ToothIcon className="h-4 w-4 shrink-0" />
                      {!collapsed && 'SETTINGS'}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/branding"
                      title={collapsed ? 'BRANDING' : undefined}
                      className={clsx(
                        location.pathname === '/branding'
                          ? 'bg-mono-800 text-mono-100 border-l-2 border-mono-100'
                          : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                        'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                        collapsed && 'justify-center'
                      )}
                    >
                      <PaintBrushIcon className="h-4 w-4 shrink-0" />
                      {!collapsed && 'BRANDING'}
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Subscribed Products */}
              {subscribedProducts.length > 0 ? (
                <li>
                  {!collapsed && (
                    <p className="text-[10px] text-mono-600 tracking-widest px-2 mb-2">
                      YOUR PRODUCTS
                    </p>
                  )}
                  <ul className="-mx-2 space-y-1">
                    {subscribedProducts.map((product) => {
                      const IconComponent = iconMap[product.icon] || CubeTransparentIcon;
                      const overrideHref = productRouteOverrides[product.slug];
                      const isActive = overrideHref
                        ? location.pathname.startsWith(overrideHref)
                        : location.pathname.startsWith(`/${product.slug}`);
                      const accentClass =
                        product.accentColor === 'cyan'
                          ? 'border-l-cyan-500'
                          : product.accentColor === 'emerald'
                          ? 'border-l-emerald-500'
                          : 'border-l-violet-500';
                      const textClass =
                        product.accentColor === 'cyan'
                          ? 'text-cyan-400'
                          : product.accentColor === 'emerald'
                          ? 'text-emerald-400'
                          : 'text-violet-400';

                      const productHref = productRouteOverrides[product.slug] || `/${product.slug}/dashboard`;
                      const isExternal = productHref.startsWith('http');
                      const linkClass = clsx(
                        isActive
                          ? `bg-mono-800 text-mono-100 border-l-2 ${accentClass}`
                          : 'text-mono-500 hover:bg-mono-800 hover:text-mono-300 border-l-2 border-transparent',
                        'group flex gap-x-3 p-2 text-xs font-medium tracking-wider transition-colors',
                        collapsed && 'justify-center'
                      );
                      const linkContent = (
                        <>
                          <IconComponent
                            className={clsx('h-4 w-4 shrink-0', isActive ? textClass : '')}
                          />
                          {!collapsed && product.name}
                        </>
                      );

                      return (
                        <li key={product.slug}>
                          {isExternal ? (
                            <a href={productHref} title={collapsed ? product.name : undefined} className={linkClass}>
                              {linkContent}
                            </a>
                          ) : (
                            <Link to={productHref} title={collapsed ? product.name : undefined} className={linkClass}>
                              {linkContent}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ) : (
                <li>
                  {!collapsed && (
                    <>
                      <p className="text-[10px] text-mono-600 tracking-widest px-2 mb-1">
                        YOUR PRODUCTS
                      </p>
                      <p className="text-[10px] text-mono-500 px-2">
                        No products activated.{' '}
                        {isAdmin ? (
                          <Link to="/team" className="text-cyan-500 hover:text-cyan-400">Manage team</Link>
                        ) : (
                          <span>Contact your admin.</span>
                        )}
                      </p>
                    </>
                  )}
                </li>
              )}

              {/* View-as role switcher (superadmin only) */}
              {isTrueSuperadmin && !collapsed && (
                <li className="-mt-5">
                  <div className="px-2 py-2 border-t border-mono-800">
                    <p className="text-[10px] text-mono-600 tracking-widest mb-1.5">VIEW AS</p>
                    <select
                      value={viewAsRole || ''}
                      onChange={(e) => setViewAsRole(e.target.value || null)}
                      className="w-full px-2 py-1.5 bg-mono-800 border border-mono-700 text-mono-200 text-[11px] tracking-wider rounded focus:outline-none focus:border-amber-500/50 cursor-pointer"
                    >
                      <option value="">SUPERADMIN</option>
                      <option value="admin">ADMIN</option>
                      <option value="analyst">ANALYST</option>
                      <option value="viewer">VIEWER</option>
                    </select>
                  </div>
                </li>
              )}

              <li className="mt-auto">
                <div className={clsx(
                  'flex items-center gap-x-3 px-2 py-3 text-xs tracking-wider border-t border-mono-800',
                  collapsed && 'justify-center'
                )}>
                  <div className="h-7 w-7 shrink-0 rounded border border-mono-600 bg-mono-800 flex items-center justify-center text-mono-300">
                    {user?.full_name?.[0] || user?.email?.[0] || 'U'}
                  </div>
                  {!collapsed && (
                    <>
                      <div className="flex-1 truncate">
                        <p className="text-mono-200 truncate">{user?.email}</p>
                        <p className="text-mono-500 uppercase text-[10px]">{user?.role}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="p-1 hover:bg-mono-800 rounded text-mono-500 hover:text-mono-300"
                        title="Logout"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={clsx('transition-all duration-300', collapsed ? 'lg:pl-14' : 'lg:pl-56')}>
        {/* Mobile header */}
        <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 border-b border-mono-800 bg-mono-900 px-4 lg:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-mono-400 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center">
              <img src="/argos-logo.png" alt="ARGOS" className="h-12 w-auto object-contain" />
            </div>
          </div>
        </div>

        {/* Impersonation banner */}
        {viewAsRole && (
          <button
            onClick={() => setViewAsRole(null)}
            className="w-full px-4 py-2 bg-amber-500/20 border-b border-amber-500/30 text-amber-300 text-[11px] tracking-widest text-center hover:bg-amber-500/30 transition-colors cursor-pointer"
          >
            VIEWING AS {viewAsRole.toUpperCase()} — CLICK TO RESET
          </button>
        )}

        {/* Top-right theme toggle */}
        <div className="fixed top-3 right-4 z-40">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-mono-900/80 border border-mono-700/50 text-mono-400 hover:text-mono-200 hover:border-mono-600 backdrop-blur-sm transition-colors"
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="8" cy="8" r="3" />
                <path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1M3.4 3.4l.7.7M11.9 11.9l.7.7M3.4 12.6l.7-.7M11.9 4.1l.7-.7" />
              </svg>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M13.5 8.5a5.5 5.5 0 0 1-7.5 5.1A5.5 5.5 0 0 1 8 2.5a4 4 0 0 0 5.5 6z" />
              </svg>
            )}
          </button>
        </div>

        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* TERRA AI Assistant — hidden on Phreatic routes where CARI takes over */}
      {!location.pathname.startsWith('/phreatic') && (
        <>
          <TerraPanel />
          <TerraToggle />
        </>
      )}
      <FeedbackButton />
    </div>
  );
}
